/*
 * The Home screen chatbot's opening comment and suggestion pills.
 *
 * In v1 the FRONT END writes the first "response" — the agent is not called
 * until the user taps a pill or types. Per the story, that opening comment is
 * the body copy of whichever Home alerts are currently active, falling back to a
 * default greeting when the user is all clear. The business rules that will make
 * the agent author this properly (and vary the pills off the user's numbers) are
 * a separate story.
 *
 * The strings below mirror the JSX in ../sections/ActionItems.js. They are
 * deliberately a separate, plain-text copy rather than something derived from
 * the rendered markup: the alert rows carry buttons and help-icon tooltips that
 * have no meaning inside a chat bubble, and a bubble that says "Review account"
 * next to a button that isn't there reads as broken. Keep the two in sync when
 * either changes — ActionItems.test.js and homeChatDefaults.test.js assert the
 * same phrases on both sides.
 */

import { SALARY_SUGGESTIONS } from '../../hey-nic/salaryPrompt'
import { SPOUSE_GOAL_SUGGESTIONS } from '../../hey-nic/spouseGoalPrompt'

export const DEFAULT_COMMENT = 'Hi, I’m Nic. What can I help you with today?'

/*
 * Two starters, matching the design's stacked pair.
 *
 * The design's own copy is personalized — "What if I retire at 67?" and "What if
 * I include my spouse in planning?" — and the first of those only makes sense
 * against the user's actual retirement age. Personalizing them belongs to the
 * business-rules story (same call SBSA made in advice/heyNic/stepChatDefaults),
 * so until it lands these are the two generic starters that are true for
 * everyone.
 */
export const DEFAULT_SUGGESTIONS = [
  'How am I tracking toward retirement?',
  'How much should I be saving?',
]

/*
 * Starters for the placeholder-salary alert, replacing the generic pair.
 *
 * Re-exported from hey-nic/salaryPrompt rather than spelled out again here: that
 * module answers both pills client-side and matches on these exact labels, so a
 * second copy of the strings is a way for a pill to stop being recognised and
 * fall through to the agent.
 */
export const DEFAULT_SALARY_SUGGESTIONS = SALARY_SUGGESTIONS

/*
 * Starters for the spouse-reached-retirement-age alert. Re-exported for the same
 * reason as the salary pair: `useSpouseGoalPrompt` matches on these exact
 * labels, and a second copy of the strings is a way for a pill to stop being
 * recognised and fall through to the agent.
 */
export const DEFAULT_SPOUSE_GOAL_SUGGESTIONS = SPOUSE_GOAL_SUGGESTIONS

const dollars = amount => `$${(amount ?? 0).toLocaleString()}`

/**
 * Plain-text body copy for one action-item key, or '' for a key with none.
 *
 * @param {string} key    one of useActionItems' ACTION_ITEM_DISPLAY_ORDER keys
 * @param {object} ctx    the same figures ActionItems interpolates
 */
export const actionItemText = (key, ctx = {}) => {
  const { currentYear, standardLimit, standardCatchup, additionalCatchup, sponsorName } = ctx
  const standard = standardCatchup?.maximumAmount
  const additional = additionalCatchup?.maximumAmount

  switch (key) {
    case 'add at least one account':
      return 'CRITICAL: Add at least one account intended for living expenses in retirement before beginning to plan.'
    case 'reviewIncomeDefaultSalary':
      // No help icon in a chat bubble — the "Learn more" pill carries what the
      // icon would have opened (see DEFAULT_SALARY_SUGGESTIONS).
      return 'TO-DO: Review your income. We may use $10,000 as a default if your retirement plan provider doesn’t supply your salary.'
    case 'ageEqualOrGreaterThanRetirement':
      return 'Looks like you’ve reached your stated retirement age — congratulations! Let’s check your next steps.'
    case 'spouseReachedRetirementAge':
      // No help icon in a chat bubble — the "Learn more" pill carries what the
      // icon would have opened (see DEFAULT_SPOUSE_GOAL_SUGGESTIONS).
      return 'It appears your spouse has reached retirement age. We suggest reviewing your income goal.'
    case 'lumpSumPensionPayoutExpected':
      return 'Have you received the lump sum pension payout yet? Our projections assume you invest it for future retirement expenses. Update your payout status.'
    case 'lumpSumPensionMayBeEligible':
      return 'Your plan allows a lump sum pension payout and based on your age, you may be eligible to receive a payment. If you’ve already received a lump sum payment, please update the payout status. If you have invested the funds elsewhere, please update your investments.'
    case 'reviewRetirementAccountThatWasAddedByInstitution':
      return `${sponsorName} added your current retirement plan information. Please review and complete any missing data.`
    case 'catchUp50to59':
      return `You have the chance to save a little more: The IRS permits those aged 50+ in the current calendar year to make catch-up contributions to their company’s retirement plan. The standard contribution limit in ${currentYear} is ${dollars(standardLimit)} and you may contribute up to an additional ${dollars(standard)} in catch-up contributions for a retirement plan account maximum contribution of ${dollars(standardLimit + standard)}.`
    case 'catchUp60to63':
      return `You have the chance to save a little more: The IRS permits those aged 60-63 in the current calendar year to make a higher catch-up contribution to their company’s retirement plan. The standard contribution limit in ${currentYear} is ${dollars(standardLimit)} and the standard catch-up contribution limit is ${dollars(standard)}. With the higher catch-up contribution limit, you may contribute up to an additional ${dollars(additional - standard)} for a total catch-up contribution of ${dollars(additional)}, making the retirement plan account maximum contribution ${dollars(standardLimit + additional)}.`
    case 'catchUpOver64':
      return `Be aware: You may need to reduce your retirement plan contributions. The IRS permits those aged 64+ in the current calendar year to make catch-up contributions of up to the regular catch-up contribution limit of ${dollars(standard)} for ${currentYear}. With the standard contribution limit of ${dollars(standardLimit)}, this means the maximum contribution you can make to your company’s retirement plan is ${dollars(standardLimit + standard)}.`
    default:
      // Includes 'all clear', which has no body copy — the caller falls back to
      // DEFAULT_COMMENT.
      return ''
  }
}

/**
 * The chatbot's opening comment for the current Home state.
 *
 * Every active alert is included rather than only the highest-priority one: the
 * alerts box this replaces on desktop shows them all, and silently dropping a
 * CRITICAL one because a catch-up notice also applies would lose information the
 * fallback design surfaces.
 *
 * @param {string[]} activeKeys  useActionItems' return value
 * @param {object}   ctx         the figures the copy interpolates
 */
export const homeChatComment = (activeKeys = [], ctx = {}) => {
  const body = activeKeys
    .map(key => actionItemText(key, ctx))
    .filter(Boolean)
    .join('\n\n')

  return body || DEFAULT_COMMENT
}

/*
 * The alerts that carry help beyond their one-line body copy, each paired with
 * the client-side exchange that offers it, in priority order.
 *
 * An ordered list rather than a map because exactly one may own the pills: both
 * exchanges label a pill "Learn more", and two live `handle`s would race for
 * that word. The placeholder salary leads because its whole point is a number
 * every other answer is computed from — this one included.
 */
const PILL_OWNERS = [
  { key: 'reviewIncomeDefaultSalary', prompt: 'salary', suggestions: SALARY_SUGGESTIONS },
  { key: 'spouseReachedRetirementAge', prompt: 'spouseGoal', suggestions: SPOUSE_GOAL_SUGGESTIONS },
]

/**
 * The help-bearing alerts that are active, in the order they take the pills.
 *
 * The whole queue, not just its head. A surface that cannot recompute alerts for
 * itself — the standalone /hey-nic route, which is handed a snapshot on entry —
 * needs to know who is waiting, or the ones behind the winner are invisible to
 * it forever.
 *
 * @param {string[]} activeKeys  useActionItems' return value
 */
export const chatPromptKeys = (activeKeys = []) =>
  PILL_OWNERS.filter(owner => activeKeys.includes(owner.key)).map(owner => owner.key)

/** The exchange and pills belonging to one help-bearing alert key, or null. */
export const promptForKey = key => PILL_OWNERS.find(owner => owner.key === key) ?? null

/**
 * Which client-side exchange owns the pills, or null for the generic starters.
 *
 * The surfaces gate their hooks on this single answer rather than on the raw
 * keys, so two of them can never be listening for "Learn more" at once.
 *
 * @param {string[]} activeKeys  useActionItems' return value
 * @returns {'salary'|'spouseGoal'|null}
 */
export const homeChatPrompt = (activeKeys = []) =>
  promptForKey(chatPromptKeys(activeKeys)[0])?.prompt ?? null

/**
 * Active help-bearing alerts that did NOT win the pills.
 *
 * Only the winner can offer its help in chat, so on the wide layout — where this
 * chat replaces the alerts box and every help icon on it — the rest would have
 * nowhere at all to be read. The caller keeps the box for exactly these, and
 * leaves them out of the comment so no alert is stated twice.
 *
 * @param {string[]} activeKeys  useActionItems' return value
 */
export const strandedHelpKeys = (activeKeys = []) => chatPromptKeys(activeKeys).slice(1)

/**
 * The chatbot's suggestion pills for the current Home state.
 *
 * The two alerts that carry a client-side exchange override the generic
 * starters, because each offers something the generic pair cannot: the salary
 * alert takes the number directly, and the spouse-retired alert carries help
 * that has nowhere else to appear on the wide layout, where the alerts box (and
 * its help icon) is replaced by this chat.
 *
 * @param {string[]} activeKeys  useActionItems' return value
 */
export const homeChatSuggestions = (activeKeys = []) =>
  promptForKey(chatPromptKeys(activeKeys)[0])?.suggestions ?? DEFAULT_SUGGESTIONS
